import { createRouter, createWebHistory } from 'vue-router'
import Landing from '@/views/Landing.vue'
import Security from '@/views/Security.vue'
import Seo from '@/views/Seo.vue'
import VariantsIndex from '@/views/VariantsIndex.vue'
import LandingPlace from '@/views/v-place/Landing.vue'
import LandingWorkbench from '@/views/v-workbench/Landing.vue'
import LandingVoice from '@/views/v-voice/Landing.vue'
import LandingLocal from '@/views/v-local/Landing.vue'
import LandingWeekly from '@/views/v-weekly/Landing.vue'
import LandingPlain from '@/views/v-plain/Landing.vue'
import LsaIntake from '@/views/intake/LsaIntake.vue'
import Login from '@/views/auth/Login.vue'
import Register from '@/views/auth/Register.vue'
import Account from '@/views/auth/Account.vue'
import IntakeList from '@/views/admin/IntakeList.vue'
import IntakeCreate from '@/views/admin/IntakeCreate.vue'
import IntakeDetail from '@/views/admin/IntakeDetail.vue'
import { restoreSession, useAuthStore } from '@/stores/auth'
import { safeReturnPath } from '@/stores/authNavigation'

// The /v/* landings are a design lab, not public pages. They carry placeholder
// contact details, unfilled testimonials, and copy that was never fact-checked
// against apps/api — so they must never reach production. Dev only.
const variantRoutes = import.meta.env.DEV
  ? [
      { path: '/v', name: 'variants', component: VariantsIndex, meta: { bare: true } },
      { path: '/v/place', name: 'v-place', component: LandingPlace, meta: { bare: true } },
      {
        path: '/v/workbench',
        name: 'v-workbench',
        component: LandingWorkbench,
        meta: { bare: true },
      },
      { path: '/v/voice', name: 'v-voice', component: LandingVoice, meta: { bare: true } },
      { path: '/v/local', name: 'v-local', component: LandingLocal, meta: { bare: true } },
      { path: '/v/weekly', name: 'v-weekly', component: LandingWeekly, meta: { bare: true } },
      { path: '/v/plain', name: 'v-plain', component: LandingPlain, meta: { bare: true } },
    ]
  : []

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', name: 'landing', component: Landing },
    { path: '/security', name: 'security', component: Security },
    { path: '/seo', name: 'seo', component: Seo },
    { path: '/intake/lsa/:token', name: 'lsa-intake', component: LsaIntake, meta: { bare: true } },
    {
      path: '/register',
      name: 'register',
      component: Register,
      meta: { bare: true, guestOnly: true },
    },
    { path: '/login', name: 'login', component: Login, meta: { bare: true, guestOnly: true } },
    {
      path: '/account',
      name: 'account',
      component: Account,
      meta: { bare: true, requiresAuth: true },
    },
    {
      path: '/admin/intakes',
      name: 'admin-intakes',
      component: IntakeList,
      meta: { bare: true, requiresAuth: true, permission: 'intakes:read' },
    },
    {
      path: '/admin/intakes/new',
      name: 'admin-intake-new',
      component: IntakeCreate,
      meta: { bare: true, requiresAuth: true, permission: 'intakes:create' },
    },
    {
      path: '/admin/intakes/:id(\\d+)',
      name: 'admin-intake-detail',
      component: IntakeDetail,
      props: (route) => ({ id: Number(route.params.id) }),
      meta: { bare: true, requiresAuth: true, permission: 'intakes:read' },
    },
    ...variantRoutes,
    // Caddy serves index.html for every path, so unmatched URLs (including the
    // /v/* lab once it's stripped from the production build) land here instead
    // of rendering a blank page.
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

router.beforeEach(async (to) => {
  if (to.meta.requiresAuth === true || to.meta.guestOnly === true) {
    try {
      await restoreSession()
    } catch {
      return {
        name: 'login',
        query: { return: safeReturnPath(to.fullPath), session: 'unavailable' },
      }
    }
  }
  const auth = useAuthStore()
  const returnPath = safeReturnPath(to.fullPath)
  if (to.meta.requiresAuth === true && !auth.account)
    return { name: 'login', query: returnPath !== undefined ? { return: returnPath } : {} }
  if (typeof to.meta.permission === 'string' && !auth.hasPermission(to.meta.permission))
    return { name: 'account', query: { access: 'denied' } }
  if (to.meta.guestOnly === true && auth.account) {
    const requested = safeReturnPath(to.query.return)
    if (requested !== undefined) return requested
    return auth.isAdmin ? { name: 'admin-intakes' } : { name: 'account' }
  }
  return true
})

if (typeof window !== 'undefined')
  window.addEventListener('ccd-auth-failure', (event) => {
    const status = (event as CustomEvent<401 | 403>).detail
    const current = router.currentRoute.value
    if (current.meta.requiresAuth !== true) return
    if (status === 401)
      void router.replace({
        name: 'login',
        query: { return: safeReturnPath(current.fullPath), session: 'expired' },
      })
    if (status === 403) void router.replace({ name: 'account', query: { access: 'denied' } })
  })
